import React, { memo, useState, useEffect } from 'react';
import dayjs from 'dayjs';
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import toast from 'react-hot-toast';
import { useAuth } from "../../contexts/AuthContext";
import type { GetItemsFilters } from '../../types';
import { formatFromDateInput, formatToDateInput } from '../../utils/date';
import { useItemFilters } from '../../contexts/ItemFilterContext';
import { ReportConst, type ReportType } from '../../constants/ReportConst';
import { useGetBiddingDateForApplyQuery, useGetBiddingDateForResultQuery } from '../../hooks/useCalendarQuery';
import { getCategoryOptions, ScreenIdConst, parseCombinedCategory, type ScreenId } from '../../constants';
import { isUnsetFilter, toCombinedValue } from '../../utils/filterValue';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault("Asia/Tokyo");

//----------------------------------------------------------------------------
// Types & Interfaces
//----------------------------------------------------------------------------

/**
 * ListingSearchForm コンポーネントのプロップス
 */
interface ListingSearchFormProps {
  // 印刷実行ボタン押下時のイベントハンドラ
  onExecute: (filters: GetItemsFilters) => void;
  // 現在印刷処理（APIリクエスト等）が実行中かどうか
  isPrinting: boolean;
  // 現在選択されている帳票種別
  currentReportType: ReportType;
}

/**
 * 帳票種別（`ReportType`）→ 画面 ID（`ScreenId`）の対応。
 * 本コンポーネントは3画面（出品一覧表印刷・手合票印刷・落札結果一覧表）で共有されるため、
 * `getCategoryOptions` に渡す画面 ID を `currentReportType` から解決する（D-0086）。
 */
const SCREEN_ID_BY_REPORT_TYPE: Record<ReportType, ScreenId> = {
  [ReportConst.SYUKKO_ICHIRAN]: ScreenIdConst.REPORT_PRINT_LIST,
  [ReportConst.TEAWASE]: ScreenIdConst.REPORT_PRINT_DEAL,
  [ReportConst.RAKUSATSU_ICHIRAN]: ScreenIdConst.REPORT_WIN_LIST,
};

//----------------------------------------------------------------------------
// Component: ListingSearchForm
// 出品一覧・手合票・落札結果一覧印刷画面用の検索条件入力フォーム。
//----------------------------------------------------------------------------
export const ListingSearchForm = memo(({ onExecute, isPrinting, currentReportType }: ListingSearchFormProps) => {
  //----------------
  // Context使用定義
  //----------------
  // Contextから現在のフィルタ状態を取得
  const { itemFilters, updateFiltersAndNavigate } = useItemFilters();

  const { currentUser } = useAuth();
  // カレンダー初期値の取得
  const { data: applyDate, isLoading: isApplyLoading } = useGetBiddingDateForApplyQuery(currentUser?.loginId);
  const { data: resultDate, isLoading: isResultLoading } = useGetBiddingDateForResultQuery(currentUser?.loginId);
  
  // 落札結果一覧の場合は直近の終了済の通常入札の入札日を使用
  const initialBiddingDate = currentReportType === ReportConst.RAKUSATSU_ICHIRAN 
  ? resultDate 
  : applyDate;

  const isDateLoading = currentReportType === ReportConst.RAKUSATSU_ICHIRAN 
  ? isResultLoading 
  : isApplyLoading;

  //----------------
  // State定義
  //----------------
  // 初期化完了フラグ
  const [isInitialized, setIsInitialized] = useState(false);

  /**
   * フォーム内の入力値を管理するローカルState。
   * 🔒 `listingCategory` は D-0086（タスク3）で追加——本画面は従来 `prefectureType` のみを
   *    持ち出品区分を持っていなかった（入札会を出せなかった根本原因）。
   */
  const [localParams, setLocalParams] = useState({
    targetDate: itemFilters.biddingDate
      ? formatToDateInput(itemFilters.biddingDate)
      : '',
    listingCategory: itemFilters.listingCategory,
    prefectureType: itemFilters.prefectureType,
    biddingNumberFrom: itemFilters.biddingNumberFrom || '',
    biddingNumberTo: itemFilters.biddingNumberTo || '',
  });

  //----------------
  // Effects
  //----------------

  /**
   * 初期化 Effect:
   * APIから取得した日付があればそれを使用、なければ今日の日付を使用。
   * 表示用StateとContext(API条件)を同期させる。
   */
  useEffect(() => {
    // 読み込み中、または認証情報がない場合はスキップ
    if (isDateLoading || !currentUser || isInitialized) return;

    // APIから取得した日付があればそれを使用、なければ今日の日付を使用
    const rawDate = initialBiddingDate 
      ? String(initialBiddingDate) 
      : dayjs().tz().format("YYYYMMDD");

    // YYYYMMDD -> YYYY-MM-DD
    const formatted = dayjs(rawDate.replace(/^(\d{4})(\d{2})(\d{2})$/, "$1-$2-$3")).format("YYYY-MM-DD");
    
    // ローカルStateとContextを同時に更新して同期を保つ
    setLocalParams(prev => ({ ...prev, targetDate: formatted }));
    
    updateFiltersAndNavigate({
      ...itemFilters,
      biddingDate: formatFromDateInput(formatted),
      // 🔒 型②の是正（D-0081）: `isUnsetFilter` に置き換え。旧 `|| 1` は入札会の 0 を県内(1) に化けさせていた。
      //    listingCategory はここでは触らない——ListingPage の初期化（K2）が唯一の出所（責務を1箇所に集約）。
      prefectureType: isUnsetFilter(itemFilters.prefectureType) ? 1 : itemFilters.prefectureType // 未設定ならデフォルト値を補完
    });

    setIsInitialized(true);
  }, [initialBiddingDate, isDateLoading, currentUser, isInitialized, itemFilters, updateFiltersAndNavigate]);

  /**
   * 🔒 バグ修正（2026-09-19・実機確認で発見）: `listingCategory`/`prefectureType` は上記の初期化 Effect と違い
   * `itemFilters`（Context）との同期処理が無く、マウント時に読み取った値（多くの場合まだ未設定の `''`）に
   * 固定されたままだった。`ListingPage` 側の K2 補完（既定値のセット）が `itemFilters` に反映されても
   * 本コンポーネントの `localParams` には届かず、プルダウンは見た目上 先頭の選択肢が選ばれて見えるのに
   * 送信時は `Number('')`＝`0` となり、`ListingCategoryEnum` に存在しない値としてAPIから400を返されていた。
   * `itemFilters` 側の値が確定し次第 `localParams` に反映する。
   */
  useEffect(() => {
    if (isUnsetFilter(itemFilters.listingCategory) || isUnsetFilter(itemFilters.prefectureType)) return;
    setLocalParams(prev => {
      if (prev.listingCategory === itemFilters.listingCategory && prev.prefectureType === itemFilters.prefectureType) {
        return prev;
      }
      return { ...prev, listingCategory: itemFilters.listingCategory, prefectureType: itemFilters.prefectureType };
    });
  }, [itemFilters.listingCategory, itemFilters.prefectureType]);

  //----------------
  // Handlers
  //----------------

  /**
   * 印刷実行ハンドラ
   */
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isDisabled) return;

    // 数字以外の文字が含まれている場合は実行を阻止する
    const isInvalidFrom = /[^0-9]/.test(localParams.biddingNumberFrom.toString());
    const isInvalidTo = /[^0-9]/.test(localParams.biddingNumberTo.toString());

    if (isInvalidFrom || isInvalidTo) {
      toast.error("入札No.には半角数字のみ入力してください。");
      return;
    }

    // 型を GetItemsFilters に合わせて整形して渡す
    onExecute({
      ...itemFilters,
      biddingDate: formatFromDateInput(localParams.targetDate),
      listingCategory: Number(localParams.listingCategory),
      prefectureType: Number(localParams.prefectureType),
      biddingNumberFrom: localParams.biddingNumberFrom ? Number(localParams.biddingNumberFrom) : undefined,
      biddingNumberTo: localParams.biddingNumberTo ? Number(localParams.biddingNumberTo) : undefined,
    });
  };

  /**
   * フォーム入力値変更ハンドラ
   */
  const handleChange = (field: string, value: string | number) => {
    // 1. まずローカルStateを更新（表示用）
    const newParams = { ...localParams, [field]: value };
    setLocalParams(newParams);

    // 2. Contextを更新（APIリクエスト用）
    // 印刷実行（onExecute）を待たずに、最新の入力値を Context に反映させる
    updateFiltersAndNavigate({
      ...itemFilters,
      biddingDate: field === 'targetDate' ? formatFromDateInput(value as string) : formatFromDateInput(localParams.targetDate),
      listingCategory: Number(newParams.listingCategory),
      prefectureType: field === 'prefectureType' ? Number(value) : Number(localParams.prefectureType),
      biddingNumberFrom: field === 'biddingNumberFrom' ? Number(value) : (localParams.biddingNumberFrom ? Number(localParams.biddingNumberFrom) : undefined),
      biddingNumberTo: field === 'biddingNumberTo' ? Number(value) : (localParams.biddingNumberTo ? Number(localParams.biddingNumberTo) : undefined),
    });
  };

  /**
   * 出品区分（組み合わせ選択欄）変更ハンドラ。
   * 🔒 `getCategoryOptions` の値は `"listingCategory_prefectureType"`（例 `"3_0"`）の組み合わせ形式のため、
   *    他フィールドと同じ `handleChange` では分解できず専用ハンドラにする。
   */
  const handleCategoryChange = (combinedValue: string) => {
    const { listingCategory, prefCategory } = parseCombinedCategory(combinedValue);
    const newParams = { ...localParams, listingCategory, prefectureType: prefCategory };
    setLocalParams(newParams);

    updateFiltersAndNavigate({
      ...itemFilters,
      biddingDate: formatFromDateInput(localParams.targetDate),
      listingCategory,
      prefectureType: prefCategory,
    });
  };

  //----------------
  // 常数・定数定義
  //----------------
  
  // WebView2環境かどうかを判定 
  const isWebView2 = !!(window.chrome && window.chrome.webview);

  /**
   * ボタンを無効化する条件の集約
   * 1. すでに印刷処理中である
   * 2. WebView2ではない
   */
  const isDisabled = isPrinting || !isWebView2;

  // 共通のTailwind CSSクラス定義
  const inputClasses = "w-full p-2 h-[42px] border border-slate-300 rounded-md bg-white focus:ring-indigo-500 focus:border-indigo-500";
  const labelClasses = "block text-sm font-bold text-slate-700 mb-1";

  // 全体の読み込み状態
  const isPageLoading = isDateLoading || !isInitialized;

  // この画面インスタンスの画面 ID（帳票種別から解決）
  const screenId = SCREEN_ID_BY_REPORT_TYPE[currentReportType];

  if (isPageLoading) {
    return (
      <div className="min-h-[100px] flex items-center justify-center bg-white p-4 rounded-lg border border-slate-200">
        <p className="text-sm text-slate-500">読み込み中...</p>
      </div>
    );
  }

  //----------------
  // Render
  //----------------
  return (
    <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
        <div className="flex flex-wrap items-end gap-6 w-full">
          {/* 入札日入力セクション */}
          <div className="w-full sm:w-auto">
            <label className={labelClasses}>入札日</label>
            <input
              type="date"
              className="w-full sm:w-48 p-2 h-[42px] border border-slate-300 rounded-md bg-white focus:ring-indigo-500 appearance-none min-w-0 safari-date-fix"
              value={localParams.targetDate}
              onChange={(e) => handleChange('targetDate', e.target.value)}
            />
          </div>

          {/* 出品区分セクション */}
          <div className="w-full sm:w-auto">
            <label className={labelClasses}>出品区分</label>
            <select
              className="w-full sm:w-56 p-2 border border-slate-300 rounded-md bg-white focus:ring-indigo-500"
              value={toCombinedValue(localParams.listingCategory, localParams.prefectureType)}
              onChange={(e) => handleCategoryChange(e.target.value)}
            >
              {getCategoryOptions(screenId, currentUser?.role).map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {currentReportType === ReportConst.TEAWASE && (
            <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
              <div className="w-28">
                <label className={labelClasses}>入札No. (From)</label>
                <input
                  name="biddingNumberFrom"
                  type="text"
                  pattern="\[0-9]*"
                  maxLength={5}
                  value={localParams.biddingNumberFrom}
                  onChange={(e) => handleChange('biddingNumberFrom', e.target.value)}
                  className={inputClasses}
                />
              </div>
              <div className="w-28">
                <label className={labelClasses}>入札No. (To)</label>
                <input
                  name="biddingNumberTo"
                  type="text"
                  pattern="\[0-9]*"
                  maxLength={5}
                  value={localParams.biddingNumberTo}
                  onChange={(e) => handleChange('biddingNumberTo', e.target.value)}
                  className={inputClasses}
                />
              </div>
            </div>
          )}

          {/* 右側：メッセージとボタンのエリア */}
          <div className="flex flex-col items-end gap-1 ml-auto self-end">
            {!isWebView2 && (
              <span className="text-sm text-red-500 font-medium whitespace-nowrap">
                ※専用アプリからのみ印刷可能です
              </span>
            )}

            {/* 印刷実行ボタン */}
            <button 
              disabled={isDisabled}
              onClick={handleSubmit}
              className={`min-w-[120px] px-6 py-2 text-white rounded shadow-sm font-bold transition-all
                ${isDisabled
                  ? 'bg-slate-300 cursor-not-allowed'
                  : 'bg-indigo-600 hover:bg-indigo-700 active:scale-95 cursor-pointer'
                }`}
            >
              {isPrinting ? '印刷中...' : '印刷'}
            </button>
          </div>
        </div>
    </div>
  );
});